/**
 * Currency Converter — TRY → USD
 *
 * Converts displayed prices from TRY to USD for non-Turkish visitors.
 * Uses fawazahmed0/exchange-api (jsdelivr CDN, no rate limits).
 * Visual-only conversion — checkout remains in TRY.
 */

(() => {
  'use strict';

  /* ── Config ── */
  const CACHE_KEY       = 'currencyRate_TRY_USD';
  const CACHE_TTL       = 6 * 60 * 60 * 1000; // 6 hours
  const API_URL         = 'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/try.min.json';
  const PRICE_SELECTORS = 'sale-price, compare-at-price, unit-price, .discount-badge, .cart-drawer__button-price';
  const CONVERTED_ATTR  = 'data-cc';
  const NOTICE_ATTR     = 'data-cc-notice';

  /* ── Formatter ── */
  const usd = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  /* ── LocalStorage cache ── */
  function cachedRate() {
    try {
      const entry = JSON.parse(localStorage.getItem(CACHE_KEY));
      if (entry && Date.now() - entry.t < CACHE_TTL) return entry.r;
    } catch { /* ignore */ }
    return null;
  }

  function cacheRate(rate) {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ r: rate, t: Date.now() }));
    } catch { /* ignore */ }
  }

  /* ── API ── */
  async function fetchRate() {
    try {
      const res  = await fetch(API_URL);
      const data = await res.json();
      const rate = data?.try?.usd;

      if (typeof rate === 'number' && rate > 0) {
        cacheRate(rate);
        return rate;
      }
    } catch { /* ignore */ }
    return null;
  }

  /* ── Number parsing (handles Turkish & US/Shopify formats) ── */
  function parseTRY(str) {
    const s = str.trim();

    // 6.100,50 → 6100.50 (Turkish: dot = thousands, comma = decimal)
    if (/^\d{1,3}(\.\d{3})+(,\d{1,2})?$/.test(s)) {
      return Number(s.replace(/\./g, '').replace(',', '.'));
    }
    // 6,100.50 → 6100.50 (US/Shopify: comma = thousands, dot = decimal)
    if (/^\d{1,3}(,\d{3})+(\.\d{1,2})?$/.test(s)) {
      return Number(s.replace(/,/g, ''));
    }
    // 6100,50 → 6100.50 (comma decimal, no thousands)
    if (/^\d+,\d{1,2}$/.test(s)) {
      return Number(s.replace(',', '.'));
    }
    // 6100.50 or 6100 (dot decimal or plain integer)
    if (/^\d+(\.\d{1,2})?$/.test(s)) {
      return Number(s);
    }

    return NaN;
  }

  /* ── DOM conversion ── */
  function convertText(text, rate, requireCurrencyMarker) {
    return text.replace(
      /(₺\s?)?([\d.,]+)(\s?(?:TL|TRY))?/g,
      (match, prefix, amount, suffix) => {
        if (requireCurrencyMarker && !prefix && !suffix) return match;
        const value = parseTRY(amount);
        if (Number.isNaN(value) || value <= 0) return match;
        return usd.format(value * rate);
      },
    );
  }

  function walkTree(node, rate, requireCurrencyMarker) {
    for (let i = 0; i < node.childNodes.length; i++) {
      const child = node.childNodes[i];

      if (child.nodeType === Node.TEXT_NODE) {
        const text = child.textContent;
        if (!text.trim()) continue;
        if (requireCurrencyMarker && !/₺|TL\b|TRY\b/.test(text)) continue;
        child.textContent = convertText(text, rate, requireCurrencyMarker);
      } else if (child.nodeType === Node.ELEMENT_NODE && !child.classList.contains('sr-only')) {
        walkTree(child, rate, requireCurrencyMarker);
      }
    }
  }

  /* ── Main conversion pass ── */
  let isRunning = false;

  function convertPrices(rate) {
    isRunning = true;

    const els = document.querySelectorAll(PRICE_SELECTORS);
    for (let i = 0; i < els.length; i++) {
      const el = els[i];
      if (el.hasAttribute(CONVERTED_ATTR)) continue;

      const isKnownPriceTag = el.tagName === 'SALE-PRICE'
        || el.tagName === 'COMPARE-AT-PRICE'
        || el.tagName === 'UNIT-PRICE';

      walkTree(el, rate, !isKnownPriceTag);
      el.setAttribute(CONVERTED_ATTR, '');
    }

    // Price-range filter prefix (₺ → $)
    const prefixes = document.querySelectorAll('.price-range .prefix');
    for (let i = 0; i < prefixes.length; i++) {
      if (!prefixes[i].hasAttribute(CONVERTED_ATTR)) {
        prefixes[i].textContent = '$';
        prefixes[i].setAttribute(CONVERTED_ATTR, '');
      }
    }

    // Checkout currency notice in cart drawer / cart page
    const footers = document.querySelectorAll('.cart-drawer__footer, .cart-page');
    for (let i = 0; i < footers.length; i++) {
      const footer = footers[i];
      if (footer.querySelector(`[${NOTICE_ATTR}]`)) continue;

      const target = footer.querySelector('.button-group');
      if (!target) continue;

      const notice = document.createElement('p');
      notice.setAttribute(NOTICE_ATTR, '');
      notice.className = 'text-subdued text-xs';
      notice.textContent = 'Prices are shown in USD. Checkout will be processed in TRY.';
      target.before(notice);
    }

    isRunning = false;
  }

  /* ── MutationObserver (catches search results, cart drawer, facets, etc.) ── */
  function observeDOM(rate) {
    let frameId = null;

    const scheduleConversion = () => {
      if (frameId !== null) return;
      frameId = requestAnimationFrame(() => {
        frameId = null;
        convertPrices(rate);
      });
    };

    new MutationObserver((mutations) => {
      if (isRunning) return;

      for (let i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes.length === 0) continue;
        scheduleConversion();
        return;
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* ── Bootstrap ── */
  async function init() {
    const cached = cachedRate();
    const rate = cached ?? await fetchRate();
    if (!rate) return;

    convertPrices(rate);
    observeDOM(rate);

    // Silently refresh the cached rate for subsequent page loads
    if (cached) fetchRate();
  }

  init();
})();
